import { useContext, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { Badge, Button } from 'slab'
import { useGetBrokersMembersMemberIdEnrollmentsIdWorkflows } from 'utils/web_api'

import CreateWorkflowDialog from './workflows/CreateWorkflowDialog'
import WorkflowSummary from './WorkflowSummary'
import { SkeletonItem } from '../reusable/SkeletonItem'

type BrokersEnrollmentWorkflowsProps = {
  enrollmentId: string
}

const BrokersEnrollmentWorkflows = ({
  enrollmentId,
}: BrokersEnrollmentWorkflowsProps) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const { isUserThatchWorkflowAdmin, memberId } = useContext(BrokersMemberContext)

  // Use optimized endpoint for workflow summaries (excludes enrollment_info since we have context)
  const {
    data: workflows,
    isLoading,
    error,
  } = useGetBrokersMembersMemberIdEnrollmentsIdWorkflows(
    memberId,
    enrollmentId,
    {
      evaluate_readiness: false, // Use cached task status for better performance
    },
    {
      query: {
        // NOTE: Refetch interval is needed to ensure the workflow is updated in real-time. Automated jobs
        // are run asynchronously, so the workflow may not be updated immediately.
        refetchInterval: (query) => {
          const workflows = query.state.data || []

          // Only refetch workflows that are in progress.
          const isWorkflowsInProgress = workflows.some(
            (workflow) => workflow.status === 'in_progress'
          )

          if (!isWorkflowsInProgress) {
            return false
          }

          // Of the workflows that are in progress, only check if there are automated tasks that are being processed.
          // If there are only manual tasks, no need to fetch via polling.
          const isAutomatedTasksInProgress = workflows.some((workflow) =>
            workflow.tasks_summary?.some(
              (task) =>
                ['pending', 'processing'].includes(task.status) &&
                task.execution_method === 'automated'
            )
          )

          if (!isAutomatedTasksInProgress) {
            return false
          }

          // Otherwise, continue to fetch every 3 seconds until automated tasks are done being processed.
          return 3000
        },
        refetchIntervalInBackground: false,
      },
    }
  )

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-semibold'>Enrollment Workflows</h3>
          <SkeletonItem heightClass='h-6' widthClass='w-16' />
        </div>
        <div className='space-y-3'>
          <SkeletonItem heightClass='h-24' widthClass='w-full' />
          <SkeletonItem heightClass='h-24' widthClass='w-full' />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='rounded-lg border border-red-200 bg-red-50 p-2'>
        <div className='flex items-center'>
          <div className='text-sm text-red-700'>
            Failed to load workflows. Please try refreshing the page.
          </div>
        </div>
      </div>
    )
  }

  if ((workflows || []).length === 0) {
    return (
      <>
        <div className='rounded-lg border border-gray-200 bg-gray-50 p-4 text-center space-y-3'>
          <div className='text-sm text-gray-500'>
            No workflows found for this enrollment.
          </div>
          {isUserThatchWorkflowAdmin && (
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              size='small'
              variant='primary'
            >
              Create Workflow
            </Button>
          )}
        </div>

        <CreateWorkflowDialog
          enrollmentId={enrollmentId}
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      <div className='space-y-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-semibold'>Enrollment Workflows</h3>
          <div className='flex items-center gap-2'>
            <Badge type='info'>
              {(workflows || []).length} workflow{(workflows || []).length !== 1 ? 's' : ''}
            </Badge>
            {isUserThatchWorkflowAdmin && (
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                size='small'
                variant='primary'
              >
                Create
              </Button>
            )}
          </div>
        </div>

        <div className='space-y-3'>
          {(workflows || []).map((workflow) => (
            <WorkflowSummary key={workflow.id} workflow={workflow} />
          ))}
        </div>
      </div>

      <CreateWorkflowDialog
        enrollmentId={enrollmentId}
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </>
  )
}

export default BrokersEnrollmentWorkflows
